import type { AppLocale } from "@/lib/locale";
import { formatSiteCutoffTime } from "@/lib/datetime.schedule";
import { m } from "@/paraglide/messages.js";

/**
 * Member-facing booking errors thrown by `createBookingCore`. The English text is the
 * server API contract (integration tests and admin tools match on it); the booking
 * wizard maps each one to a catalog string with `localizeBookingError`.
 */
export const BOOKING_ERROR = {
  lessonTypeUnavailable: "Lesson type unavailable",
  noAvailability: "No availability on this day.",
  slotUnavailable: "That time is no longer available. Please pick another.",
  slotTaken: "That time was just booked. Please pick another.",
} as const;

export function bookingCutoffClosedError(cutoffLabel: string): string {
  return `Bookings for this date closed at ${cutoffLabel} the day before.`;
}

const CUTOFF_CLOSED = /^Bookings for this date closed at (.+) the day before\.$/;

/**
 * Localize a server booking error for the visitor. `cutoffTime` (Central `HH:MM`) lets
 * the cutoff message show the time in the visitor's own clock format; without it the
 * server's en-US label is kept. Unknown messages pass through unchanged.
 */
export function localizeBookingError(
  message: string,
  locale: AppLocale,
  cutoffTime?: string | null,
): string {
  const opts = { locale };
  switch (message) {
    case BOOKING_ERROR.lessonTypeUnavailable:
      return m.booking_toast_type_unavailable({}, opts);
    case BOOKING_ERROR.noAvailability:
      return m.booking_error_no_availability({}, opts);
    case BOOKING_ERROR.slotUnavailable:
      return m.booking_error_slot_unavailable({}, opts);
    case BOOKING_ERROR.slotTaken:
      return m.booking_error_slot_taken({}, opts);
  }
  const closed = CUTOFF_CLOSED.exec(message);
  if (closed) {
    const cutoffLabel = cutoffTime ? formatSiteCutoffTime(cutoffTime, locale) : closed[1];
    return m.booking_error_cutoff_closed({ cutoffLabel }, opts);
  }
  return message;
}
